import React from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { TIDS } from "@/constants/testIds";
import { Check, ArrowRight } from "lucide-react";

const Pricing = ({ onSelectPackage }) => {
  const { t } = useI18n();

  return (
    <section id="pricing" className="py-20 md:py-32 bg-white" data-testid={TIDS.pricingSection}>
      <div className="zdc-container">
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <div className="text-xs font-medium uppercase tracking-[0.18em] text-[#2563EB] mb-3">
            {t.pricing.eyebrow}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#111111]">
            {t.pricing.title}
          </h2>
          <p className="mt-4 text-base md:text-lg text-[#6B7280]">{t.pricing.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {t.pricing.packages.map((pkg) => (
            <div
              key={pkg.id}
              data-testid={TIDS.pricingCard}
              data-package={pkg.id}
              className={`relative flex flex-col p-7 md:p-8 rounded-2xl card-hover ${
                pkg.highlight
                  ? "bg-[#111111] text-white border-2 border-[#2563EB] shadow-[0_30px_60px_rgba(37,99,235,0.2)]"
                  : "bg-white border border-[#E5E7EB] hover:border-[#111111]/20 hover:shadow-[0_20px_40px_rgba(17,17,17,0.06)]"
              }`}
            >
              {pkg.highlight && (
                <div className="absolute -top-3 left-7">
                  <div className="px-3 py-1 rounded-full bg-[#2563EB] text-white text-[11px] font-medium uppercase tracking-wider">
                    {t.pricing.mostPopular}
                  </div>
                </div>
              )}

              <div className="space-y-1">
                <div className={`text-[10px] font-mono uppercase tracking-wider ${pkg.highlight ? "text-white/50" : "text-[#6B7280]"}`}>
                  {pkg.code}
                </div>
                <h3 className={`font-display text-lg font-semibold ${pkg.highlight ? "text-white" : "text-[#111111]"}`}>
                  {pkg.name}
                </h3>
                <p className={`text-sm ${pkg.highlight ? "text-white/70" : "text-[#6B7280]"}`}>
                  {pkg.tagline}
                </p>
              </div>

              <div className="mt-6 flex items-baseline gap-1.5">
                <span className={`font-display text-4xl md:text-5xl font-semibold tracking-tight ${pkg.highlight ? "text-white" : "text-[#111111]"}`}>
                  €{pkg.price.toLocaleString("it-IT")}
                </span>
                <span className={`text-xs ${pkg.highlight ? "text-white/60" : "text-[#6B7280]"}`}>
                  {t.pricing.perPackage}
                </span>
              </div>

              <ul className={`mt-6 space-y-3 flex-1 ${pkg.highlight ? "" : ""}`}>
                {pkg.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm">
                    <span className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                      pkg.highlight ? "bg-[#2563EB]" : "bg-[#F5F7FA]"
                    }`}>
                      <Check className={`w-2.5 h-2.5 ${pkg.highlight ? "text-white" : "text-[#111111]"}`} strokeWidth={3} />
                    </span>
                    <span className={pkg.highlight ? "text-white/90" : "text-[#111111]"}>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                data-testid={TIDS.pricingBuyBtn}
                data-package-id={pkg.id}
                onClick={() => onSelectPackage(pkg)}
                className={`mt-7 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-medium transition-all ${
                  pkg.highlight
                    ? "bg-white text-[#111111] hover:bg-[#F5F7FA]"
                    : "bg-[#111111] text-white hover:bg-black"
                } hover:translate-y-[-1px]`}
              >
                {pkg.contactOnly ? t.pricing.contactUs : t.pricing.buyNow}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
